/**
 * Insight Detail Sheet
 * Shows full insight details with option to ask AI
 */

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import {
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  Bookmark,
  Pencil,
  Check,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useStockQuote } from '@/hooks'
import { calculateGainLoss } from '@/lib/services/stockPrice'
import { cn } from '@/lib/utils'
import { TranslatableText } from '@/components/TranslatableText'
import { getDateLocale } from '@/lib/utils/dateLocale'

interface InsightDetailSheetProps {
  open: boolean
  onClose: () => void
  insight: {
    id: string
    content: string
    ticker: string | null
    sentiment: 'bullish' | 'bearish' | 'neutral'
    username: string
    displayName?: string
    createdAt: Date
    imageUrl?: string
    tags?: string[]
  } | null
  priceAtTrack?: number | null
  trackedAt?: Date | null
  isTracked?: boolean
  onAskAI?: () => void
  onTrackToggle?: () => void
  onUpdateEntryPrice?: (price: number) => void
}

export function InsightDetailSheet({
  open,
  onClose,
  insight,
  priceAtTrack,
  trackedAt,
  isTracked = false,
  onAskAI,
  onTrackToggle,
  onUpdateEntryPrice,
}: InsightDetailSheetProps) {
  const { t, i18n } = useTranslation()
  const { data: quote } = useStockQuote(insight?.ticker)
  const [isEditingPrice, setIsEditingPrice] = useState(false)
  const [editedPrice, setEditedPrice] = useState('')

  // Reset edit state when sheet closes or insight changes
  useEffect(() => {
    if (!open) {
      setIsEditingPrice(false)
      setEditedPrice('')
    }
  }, [open])

  // Initialize edited price when starting to edit
  useEffect(() => {
    if (isEditingPrice && priceAtTrack) {
      setEditedPrice(priceAtTrack.toFixed(2))
    }
  }, [isEditingPrice, priceAtTrack])

  const handleSavePrice = () => {
    const price = parseFloat(editedPrice)
    if (!isNaN(price) && price > 0) {
      onUpdateEntryPrice?.(price)
      setIsEditingPrice(false)
    }
  }

  const gainLoss =
    quote && priceAtTrack
      ? calculateGainLoss(quote.currentPrice, priceAtTrack)
      : null

  const sentimentConfig = useMemo(
    () => ({
      bullish: {
        icon: TrendingUp,
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/20',
        label: t('bullish'),
      },
      bearish: {
        icon: TrendingDown,
        color: 'text-red-400',
        bg: 'bg-red-500/10',
        border: 'border-red-500/20',
        label: t('bearish'),
      },
      neutral: {
        icon: Minus,
        color: 'text-neutral-400',
        bg: 'bg-neutral-500/10',
        border: 'border-neutral-500/20',
        label: t('neutral'),
      },
    }),
    [t]
  )

  if (!insight) return null

  const config = sentimentConfig[insight.sentiment]
  const SentimentIcon = config.icon

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="h-[100vh] bg-neutral-950 border-white/[0.06] p-0 overflow-hidden flex flex-col">
        {/* Header */}
        <SheetHeader className="p-4 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white">{t('insightDetails')}</SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-neutral-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Author & Time */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
              <span className="text-black font-semibold text-sm">
                {(insight.displayName || insight.username)
                  .charAt(0)
                  .toUpperCase()}
              </span>
            </div>
            <div>
              <div className="text-white font-medium">
                {insight.displayName || insight.username}
              </div>
              <div className="text-xs text-neutral-500">
                @{insight.username} •{' '}
                {formatDistanceToNow(insight.createdAt, {
                  addSuffix: true,
                  locale: getDateLocale(i18n.language),
                })}
              </div>
            </div>
          </div>

          {/* Ticker & Price Info */}
          {insight.ticker && (
            <div className="bg-neutral-900/50 rounded-lg p-4 border border-white/[0.06]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cyan-400 font-mono text-lg font-semibold">
                  ${insight.ticker.toUpperCase()}
                </span>
                {quote && (
                  <span className="text-white font-mono text-lg">
                    ${quote.currentPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Price change from market */}
              {quote && (
                <div className="flex items-center gap-2 text-sm mb-2">
                  <span className="text-neutral-500">{t('todayLabel')}</span>
                  <span
                    className={
                      quote.changePercent >= 0
                        ? 'text-emerald-400'
                        : 'text-red-400'
                    }
                  >
                    {quote.changePercent >= 0 ? '+' : ''}
                    {quote.changePercent.toFixed(2)}%
                  </span>
                </div>
              )}

              {/* Tracked price info */}
              {(priceAtTrack || isTracked) && (
                <div className="pt-2 border-t border-white/[0.06] mt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">{t('entryPrice')}</span>
                    {isEditingPrice ? (
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-400">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedPrice}
                          onChange={(e) => setEditedPrice(e.target.value)}
                          className="w-24 h-7 px-2 py-1 text-sm font-mono bg-neutral-800 border-white/10 text-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSavePrice()
                            if (e.key === 'Escape') setIsEditingPrice(false)
                          }}
                        />
                        <button
                          onClick={handleSavePrice}
                          className="p-1 rounded text-emerald-400 hover:bg-emerald-500/10"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-300 font-mono">
                          {priceAtTrack ? `$${priceAtTrack.toFixed(2)}` : '—'}
                        </span>
                        <button
                          onClick={() => setIsEditingPrice(true)}
                          className="p-1 rounded text-neutral-500 hover:text-white hover:bg-white/10"
                          title={t('editEntryPrice')}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  {gainLoss && (
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-neutral-500">{t('profitLoss')}</span>
                      <span
                        className={cn(
                          'font-mono',
                          gainLoss.percent >= 0
                            ? 'text-emerald-400'
                            : 'text-red-400'
                        )}
                      >
                        {gainLoss.percent >= 0 ? '+' : ''}
                        {gainLoss.percent.toFixed(2)}% ($
                        {gainLoss.value >= 0 ? '+' : ''}
                        {gainLoss.value.toFixed(2)})
                      </span>
                    </div>
                  )}
                  {trackedAt && (
                    <div className="text-xs text-neutral-500 mt-2">
                      {t('tracked')}{' '}
                      {formatDistanceToNow(trackedAt, {
                        addSuffix: true,
                        locale: getDateLocale(i18n.language),
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sentiment Badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border',
                config.bg,
                config.color,
                config.border
              )}
            >
              <SentimentIcon className="w-4 h-4" />
              {config.label}
            </div>

            {insight.tags?.map((tag) => (
              <div
                key={tag}
                className="px-3 py-1.5 rounded-full bg-neutral-800/50 text-neutral-400 text-sm border border-white/[0.06]"
              >
                #{tag}
              </div>
            ))}
          </div>

          {/* Full Content */}
          <div className="text-neutral-200 leading-relaxed whitespace-pre-line">
            <TranslatableText text={insight.content} />
          </div>

          {/* Image */}
          {insight.imageUrl && (
            <div className="rounded-lg overflow-hidden border border-white/[0.06]">
              <img
                src={insight.imageUrl}
                alt={t('insightAttachment')}
                className="w-full h-auto"
              />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-white/[0.06] flex gap-2">
          {onTrackToggle && (
            <Button
              variant="outline"
              onClick={onTrackToggle}
              className={cn(
                'flex-1',
                isTracked
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20'
                  : 'bg-neutral-900 border-white/[0.08] text-neutral-300 hover:bg-neutral-800'
              )}
            >
              <Bookmark
                className={cn('w-4 h-4 mr-2', isTracked && 'fill-current')}
              />
              {isTracked ? t('tracked') : t('track')}
            </Button>
          )}
          {onAskAI && (
            <Button
              onClick={onAskAI}
              className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {t('askAI')}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
